import { useCallback, useMemo } from 'react';

import type { ActionMeta, GroupBase, OnChangeValue } from 'react-select';

import { isEmpty, isNotDefined } from '@enterprise/core';

import type { SelectProps } from '#select/types/select.props';

import { isMultiData } from '#types/guards/is-multi-data.guard';

/**
 * Custom React hook to handle "fixed" options logic for a Select component.
 *
 * Determines whether the select is clearable based on the provided `isFixed` function and current value,
 * and provides a wrapped `onChange` handler that prevents removal of fixed options.
 *
 * @template IsMulti - Indicates if the select is multi-select.
 * @template Option - The option type.
 * @template Group - The group type, extending GroupBase<Option>.
 *
 * @param params - Object containing select props:
 * @param params.isClearable - Whether the select is clearable by default.
 * @param params.isFixed - Function to determine if an option is fixed (cannot be removed).
 * @param params.onChange - Change handler for the select.
 * @param params.options - The available options for the select.
 * @param params.value - The current value(s) of the select.
 *
 * @returns An object with:
 *   - `changed`: A callback to handle changes, preventing removal of fixed options.
 *   - `clearable`: A boolean indicating if the select is currently clearable.
 */
export const useIsFixed = <
  IsMulti extends boolean = false,
  Option = unknown,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  isClearable,
  isFixed,
  onChange,
  options,
  value,
}: Pick<SelectProps<IsMulti, Option, Group>, 'isClearable' | 'isFixed' | 'onChange' | 'options' | 'value'>) => {
  const clearable = useMemo(() => {
    if (isNotDefined(isFixed) || isNotDefined(value)) {
      return isClearable ?? false;
    }

    if (isMultiData(value)) {
      return value.some((item) => {
        return !isFixed(item);
      });
    }

    return !isFixed(value);
  }, [isFixed, isClearable, value]);

  const changed = useCallback(
    (newValue: OnChangeValue<Option, IsMulti>, actionMeta: ActionMeta<Option>) => {
      const { action, removedValue } = actionMeta;
      let currentValue = newValue;

      if (isNotDefined(options) || isEmpty(options)) {
        return;
      }

      switch (action) {
        case 'clear': {
          if (isMultiData(value)) {
            currentValue = value.filter((current) => {
              return isFixed?.(current);
            }) as OnChangeValue<Option, IsMulti>;
          }
          break;
        }

        case 'pop-value':

        case 'remove-value':
          if (isFixed?.(removedValue)) {
            return;
          }
          break;
      }

      onChange?.(currentValue, actionMeta);
    },
    [isFixed, onChange, options, value]
  );

  return { changed, clearable };
};
